<!-- Polaris (fase 5.1, D-C1): a contact of the list, on the kit's plant (areas/contacts/PeopleList.vue).
     `layout="row"` is a `ui/table` row (from md up); `layout="item"` is the compact phone item (R2-5-PC-14 = B,
     the 3.1 QueueItem pattern). The whole row opens the contact; the checkbox does not (R2-5-PC-12 = A).
     The expanding card and its inline form left: editing lives in the drawer (D-C1). -->
<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { dynamicTime } from 'shared/helpers/timeHelper';
import countries from 'shared/constants/countries';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Flag from 'dashboard/components-next/flag/Flag.vue';
import MiddleTruncate from 'dashboard/components-next/polaris/cards/MiddleTruncate.vue';
import { Checkbox } from 'dashboard/components-next/polaris/ui/checkbox';
import {
  TableCell,
  TableRow,
} from 'dashboard/components-next/polaris/ui/table';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, default: '' },
  email: { type: String, default: '' },
  additionalAttributes: { type: Object, default: () => ({}) },
  phoneNumber: { type: String, default: '' },
  thumbnail: { type: String, default: '' },
  availabilityStatus: { type: String, default: null },
  lastActivityAt: { type: Number, default: null },
  createdAt: { type: Number, default: null },
  isSelected: { type: Boolean, default: false },
  // The contact open in the drawer (aria-current, the selected look of the 3.1 table).
  isOpen: { type: Boolean, default: false },
  layout: {
    type: String,
    default: 'row',
    validator: value => ['row', 'item'].includes(value),
  },
  // A picture of a row (the faded empty state): no checkbox, not focusable.
  inert: { type: Boolean, default: false },
});

const emit = defineEmits(['showContact', 'select']);

const { t, locale } = useI18n();

const countriesMap = countries.reduce((acc, country) => {
  acc[country.id] = country;
  return acc;
}, {});

const countryDetails = computed(() => {
  const { country, countryCode, city } = props.additionalAttributes || {};
  if (!country && !countryCode) return null;
  const activeCountry = countriesMap[country] || countriesMap[countryCode];
  if (!activeCountry) return null;
  return { countryCode: activeCountry.id, city, name: activeCountry.name };
});

// The formattedLocation of the old card: "City, Country"; a city with no known country still shows.
const location = computed(() => {
  if (countryDetails.value) {
    return [countryDetails.value.city, countryDetails.value.name]
      .filter(Boolean)
      .join(', ');
  }
  return props.additionalAttributes?.city || '';
});
// A value the contact does not have shows a dash, never a blank cell.
const noValue = computed(() => t('CONTACTS_POLARIS.LIST.NO_VALUE'));
const companyName = computed(() => props.additionalAttributes?.companyName);

const relative = time => (time ? dynamicTime(time) : '');
// The whole date in the viewer's language, for the tooltip (the relative time is the woot's, in English).
const fullDate = time =>
  time
    ? new Date(time * 1000).toLocaleString(locale.value.replace('_', '-'), {
        dateStyle: 'full',
        timeStyle: 'short',
      })
    : '';
const isoDate = time => (time ? new Date(time * 1000).toISOString() : '');

const rowState = computed(() =>
  props.isOpen || props.isSelected ? 'selected' : undefined
);

const open = () => {
  if (!props.inert) emit('showContact', props.id);
};
const toggleSelect = value => emit('select', value === true);
</script>

<template>
  <TableRow
    v-if="layout === 'row'"
    :tabindex="inert ? undefined : 0"
    class="group/contact outline-none focus-visible:bg-polaris-muted/50 focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-polaris-ring/50"
    :class="inert ? '' : 'cursor-pointer'"
    :data-state="rowState"
    :aria-current="isOpen ? 'true' : undefined"
    :data-contact-row="id"
    @click="open"
    @keydown.enter.self.prevent="open"
  >
    <TableCell class="w-10 ps-4" @click.stop>
      <label v-if="!inert" class="flex size-8 items-center justify-center">
        <Checkbox
          :model-value="isSelected"
          :aria-label="t('CONTACTS_POLARIS.LIST.SELECT_CONTACT', { name })"
          @update:model-value="toggleSelect"
        />
      </label>
    </TableCell>
    <TableCell class="max-w-0 min-w-56">
      <div class="flex min-w-0 items-center gap-3">
        <Avatar
          :name="name"
          :src="thumbnail"
          :size="32"
          :status="availabilityStatus"
          hide-offline-status
          class="shrink-0"
        />
        <div class="grid min-w-0 flex-1">
          <span
            class="truncate font-medium text-polaris-foreground"
            :title="name"
          >
            {{ name }}
          </span>
          <MiddleTruncate
            v-if="email"
            :text="email"
            :tail="12"
            class="type-polaris-caption text-polaris-muted-foreground"
          />
          <span
            v-else
            class="type-polaris-caption text-polaris-muted-foreground"
          >
            {{ noValue }}
          </span>
        </div>
      </div>
    </TableCell>
    <TableCell
      class="hidden text-polaris-muted-foreground tabular-nums @[38rem]/list:table-cell"
    >
      {{ phoneNumber || noValue }}
    </TableCell>
    <TableCell class="hidden max-w-40 @[49rem]/list:table-cell">
      <span
        v-if="companyName"
        class="block truncate text-polaris-muted-foreground"
        :title="companyName"
      >
        {{ companyName }}
      </span>
      <span v-else class="text-polaris-muted-foreground">{{ noValue }}</span>
    </TableCell>
    <TableCell class="hidden max-w-40 @[60rem]/list:table-cell">
      <span
        v-if="location"
        class="flex min-w-0 items-center gap-2 text-polaris-muted-foreground"
        :title="location"
      >
        <Flag
          v-if="countryDetails"
          :country="countryDetails.countryCode"
          class="size-3.5 shrink-0"
        />
        <span class="truncate">{{ location }}</span>
      </span>
      <span v-else class="text-polaris-muted-foreground">{{ noValue }}</span>
    </TableCell>
    <TableCell class="hidden text-polaris-muted-foreground @lg/list:table-cell">
      <time
        v-if="lastActivityAt"
        :datetime="isoDate(lastActivityAt)"
        :title="fullDate(lastActivityAt)"
      >
        {{ relative(lastActivityAt) }}
      </time>
      <template v-else>{{ noValue }}</template>
    </TableCell>
    <TableCell
      class="hidden pe-4 text-polaris-muted-foreground @[70rem]/list:table-cell"
    >
      <time
        v-if="createdAt"
        :datetime="isoDate(createdAt)"
        :title="fullDate(createdAt)"
      >
        {{ relative(createdAt) }}
      </time>
      <template v-else>{{ noValue }}</template>
    </TableCell>
  </TableRow>

  <li
    v-else
    class="flex items-start gap-3 border-0 border-b border-solid border-polaris-border px-4 py-3 outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-polaris-ring/50 data-[state=selected]:bg-polaris-muted"
    :class="inert ? '' : 'cursor-pointer'"
    :tabindex="inert ? undefined : 0"
    :data-state="rowState"
    :aria-current="isOpen ? 'true' : undefined"
    :data-contact-item="id"
    @click="open"
    @keydown.enter.self.prevent="open"
  >
    <label
      v-if="!inert"
      class="-ms-2 flex size-8 shrink-0 items-center justify-center"
      @click.stop
    >
      <Checkbox
        :model-value="isSelected"
        :aria-label="t('CONTACTS_POLARIS.LIST.SELECT_CONTACT', { name })"
        @update:model-value="toggleSelect"
      />
    </label>
    <Avatar
      :name="name"
      :src="thumbnail"
      :size="32"
      :status="availabilityStatus"
      hide-offline-status
      class="shrink-0"
    />
    <div class="grid min-w-0 flex-1 gap-0.5">
      <div class="flex min-w-0 items-baseline gap-2">
        <span
          class="min-w-0 flex-1 truncate font-medium type-polaris-body text-polaris-foreground"
          :title="name"
        >
          {{ name }}
        </span>
        <time
          v-if="lastActivityAt"
          class="shrink-0 type-polaris-caption text-polaris-muted-foreground"
          :datetime="isoDate(lastActivityAt)"
          :title="fullDate(lastActivityAt)"
        >
          {{ relative(lastActivityAt) }}
        </time>
      </div>
      <MiddleTruncate
        v-if="email"
        :text="email"
        :tail="12"
        class="type-polaris-caption text-polaris-muted-foreground"
      />
      <span
        v-if="phoneNumber"
        class="truncate type-polaris-caption tabular-nums text-polaris-muted-foreground"
      >
        {{ phoneNumber }}
      </span>
    </div>
  </li>
</template>
